import { gsap } from 'gsap';
import { Container, Sprite, Ticker } from '../../odie';
import { app } from '../app';
import { PositionType } from '../../lib/defs/types';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { tweenPower2In } from '../../lib/util/tweens';
import { arrayRandom } from '../../lib/util/jsTools';
import { integerRandom } from '../../lib/util/mathTools';
import { blockColorMap, ColorId, colorIds } from '../defs/block';
import { BlockEntity } from '../entities/BlockEntity';
import { mapToViewPosition } from '../util/mapTools';
import { CubeBeamAnimation } from './CubeBeamAnimation';
import { waitFor } from '../../lib/util/waitFor';
import { getMatch2AssetPath } from '../util/assets';
import { View } from '@play-co/timestep-core/ui';

// types
//-----------------------------------------------------------------------------
// public
export type CubeAnimationOptions = {
  color: ColorId;
  position: PositionType;
  targets: BlockEntity[];
};

// public
type BeamEntry = {
  beam: CubeBeamAnimation;
  target: BlockEntity;
};

// constants
//-----------------------------------------------------------------------------
const manifest = {
  all: 'power/block.cube.all.png',
  blue: 'power/block.cube.blue.png',
  green: 'power/block.cube.green.png',
  purple: 'power/block.cube.purple.png',
  red: 'power/block.cube.red.png',
  yellow: 'power/block.cube.yellow.png',
  blueSparkle: 'assets/particles/sparkle_blue.png',
  greenSparkle: 'assets/particles/sparkle_green.png',
  purpleSparkle: 'assets/particles/sparkle_purple.png',
  redSparkle: 'assets/particles/sparkle_red.png',
  yellowSparkle: 'assets/particles/sparkle_yellow.png',
  sparkles: 'fx.cube/fx.cube.sparkles.png',
  wave: 'fx.bomb/fx.bomb.wave.png',
};

/*
    cube effect animation
*/
export class CubeAnimation extends Container implements IAnimation {
  // fields
  //-------------------------------------------------------------------------
  // input
  private _options: CubeAnimationOptions;
  // scene
  private _beams: BeamEntry[] = [];
  // state
  private _completedPromise!: Promise<any>;
  // handlers
  private _beamStepper = () => this._stepBeams();

  public onBeamReachTarget?: (target: BlockEntity) => void;

  // promise
  //-------------------------------------------------------------------------
  public get completed() {
    return this._completedPromise;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: CubeAnimationOptions) {
    super();
    this._options = options;
  }

  public static assets(): string[] {
    return [...CubeBeamAnimation.assets(), ...Object.values(manifest)];
  }

  // impl
  //-------------------------------------------------------------------------
  public async start(): Promise<void> {
    // create cube
    const cube = this._createCube();

    if (this._options.color === 'all') {
      app.sound.play('puzzle-combo-cube-cube');
    } else {
      app.sound.play('puzzle-cube-activate');
      app.sound.play('puzzle-cube-spin');
      // create beams
      this._createBeams();

      // start animate beams
      this._animateBeamsStart();
    }

    // animate cube. beams last long as this does.
    await this._animateCube(cube);

    // stop animating beams
    this._animateBeamsStop();

    // animate explode
    this._completedPromise = this._animateExplode();
  }

  public stop() {}

  // private: steppers
  //-------------------------------------------------------------------------
  private _stepBeams() {
    // for each beam
    for (const entry of this._beams) {
      // if target despawned, stop and despawn early
      if (!entry.target.scene) {
        entry.beam.stop();
        this.removeChild(entry.beam);
      }
    }
  }

  // private: scene
  //-------------------------------------------------------------------------
  private _createBeams() {
    const { color, position, targets } = this._options;
    const from = mapToViewPosition(position, true);

    // for each target
    for (const target of targets) {
      const fromGlow = target === targets[0];
      const targetColor =
        blockColorMap[
          color === 'all' ? (arrayRandom(colorIds) as ColorId) : color
        ].glow;
      const to = mapToViewPosition(target.c.position.mapPosition, true);
      const direction = to.sub(from);
      const length = direction.length();

      // spawn beam. only one needs a from glow
      const beam = new CubeBeamAnimation({
        color: targetColor,
        length,
        fromGlow,
      });
      beam.rotation = -direction.angle();

      // add to list
      this._beams.push({ beam, target });
    }
  }

  private _createCube(): Sprite {
    const { color } = this._options;
    const cube = Sprite.from(manifest[color]);
    cube.anchorX = cube.anchorY = 0.5;
    cube.pivotX = cube.pivotY = -2;
    cube.zIndex = 1;
    cube.visible = false;
    return cube;
  }

  // private: animation
  //-------------------------------------------------------------------------
  private async _animateCube(cube: Sprite) {
    const big = this._options.color === 'all';
    let duration = big ? 2.7 : 1.4;
    const alphaDuration = duration * 0.2;
    const scale = big ? 2.5 : 2;

    // set initial values
    cube.visible = true;
    cube.scaleX = cube.scaleY = 1;
    cube.alpha = 1;
    cube.rotation = 1;

    // spawn
    this.addChild(cube);

    // animate
    if (big) {
      const parent = this.parent as Container;
      parent.view.style.zIndex = 1;

      // Create a background dimmer
      const outerContainer = parent.parent as Container;
      const dimmer = new View({
        width: outerContainer.width * 1.05,
        height: outerContainer.height * 1.1,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        centerAnchor: true,
        centerOnOrigin: true,
        opacity: 0,
      });
      outerContainer.view.addSubview(dimmer);

      this._animateChargeUpWaves();
      this._animateChargeUpSparkles();

      const delay = 0.4;
      const scaleUpDuration = duration * 0.7;
      const scaleDownDuration = duration - scaleUpDuration - delay;

      await gsap
        .timeline()
        .to(dimmer.style, { duration: 2, opacity: 1, ease: 'quad.inOut' })
        .to(
          cube,
          {
            scaleX: scale,
            scaleY: scale,
            duration: scaleUpDuration,
            ease: 'power2.inOut',
          },
          0,
        )
        .to(
          cube,
          {
            scaleX: 0,
            scaleY: 0,
            duration: scaleDownDuration,
            ease: 'power2.in',
          },
          scaleUpDuration + delay,
        )
        .to(cube, { rotation: -Math.PI * 8, duration, ease: 'quad.in' }, 0);

      // Remoove background dimmer
      gsap.to(dimmer.style, {
        duration: 0.5,
        opacity: 0,
        ease: 'quad.out',
        onComplete: () => {
          outerContainer.view.removeSubview(dimmer);
        },
      });
    } else {
      await gsap
        .timeline()
        .to(cube, { scaleX: scale, scaleY: scale, duration, ease: 'none' })
        .to(
          cube,
          { alpha: 0, duration: alphaDuration, ease: 'power2.in' },
          duration - alphaDuration,
        )
        .to(cube, { rotation: -Math.PI * 6, duration }, 0);
    }

    // despawn
    this.removeChild(cube);
  }

  private async _animateBeamsStart() {
    // start beams
    for (const entry of this._beams) {
      // add random delay unless first
      waitFor(integerRandom(0, entry === this._beams[0] ? 0 : 800)).then(() => {
        // spawn beam
        this.addChild(entry.beam);
        this.sortChildren();

        // animate
        entry.beam.start().then(() => {
          this.onBeamReachTarget?.(entry.target);
        });
      });
    }

    // step beams
    Ticker.system.add(this._beamStepper);
  }

  private async _animateBeamsStop() {
    if (!this._beams.length) return;

    // unstep beams
    Ticker.system.remove(this._beamStepper);

    // stop and despawn beams
    for (const entry of this._beams) {
      entry.beam.stop();
      this.removeChild(entry.beam);
    }
  }

  private async _animateChargeUpSparkles() {
    const promises: gsap.core.Timeline[] = [];

    for (let index = 0; index < 6; index++) {
      const sparkles = Sprite.from(manifest.sparkles);

      sparkles.alpha = 0;
      sparkles.anchorX = sparkles.anchorY = 0.5;
      sparkles.scaleX = sparkles.scaleY = 2 + Math.random();
      sparkles.rotation = index * 0.3;
      this.addChild(sparkles);

      promises.push(
        gsap
          .timeline({ delay: index * 0.35 })
          .to(sparkles, { duration: 0.6, alpha: 1, ease: 'quad.in' })
          .to(sparkles, { duration: 1, scaleX: 0, scaleY: 0, ease: 'none' }, 0)
          .to(
            sparkles,
            {
              duration: 1,
              rotation: sparkles.rotation - Math.PI,
              ease: 'quad.in',
              onComplete: () => this.removeChild(sparkles),
            },
            0,
          ),
      );
    }

    await Promise.all(promises);
  }

  private async _animateChargeUpWaves() {
    const ticker = { tick: 0 };
    const wave = Sprite.from(manifest.wave);

    wave.alpha = 0;
    wave.anchorX = wave.anchorY = 0.5;
    wave.scaleX = wave.scaleY = 3.5;
    this.addChild(wave);

    const waveDuration = 0.4;
    const fadeInDuration = 0.3;
    const duration = waveDuration * 7 - 0.1;

    await gsap.to(ticker, {
      duration,
      tick: duration,
      ease: 'quad.in',
      onUpdate: () => {
        const ratio = (ticker.tick % waveDuration) / waveDuration;
        const inverseRatio = 1 - ratio;
        wave.scaleX = wave.scaleY = inverseRatio * 3.5;
        wave.alpha = Math.min(ratio / fadeInDuration, 1);
      },
    });

    this.removeChild(wave);
  }

  private async _animateExplode() {
    const { color } = this._options;
    const all = color === 'all';
    const textures = (
      all
        ? [
            manifest.blueSparkle,
            manifest.greenSparkle,
            manifest.purpleSparkle,
            manifest.redSparkle,
            manifest.yellowSparkle,
          ]
        : [manifest[`${color}Sparkle`]]
    ).map((path) => getMatch2AssetPath(path));

    // determine scale
    const scale = all ? 1.75 : 1.4;

    // determine magnitude
    const magnitudeLow = all ? 300 : 200;
    const magnitudeHigh = all ? 700 : 400;

    // determine duration
    const duration = all ? 1 : 0.75;

    const rate = all ? 0.005 : 0.0005;
    const limit = all ? 65 : 20;
    const emitterDuration = all ? 0.25 : 0.1;

    // create emitter
    const emitter = app.particles.create({
      textures,
      rate,
      limit,
      emitterDuration,
      scale: [
        { x: 0.5, y: 0.5 },
        { x: scale, y: scale },
      ],
      duration,
      behaviors: [
        { type: 'explode', magnitude: [magnitudeLow, magnitudeHigh] },
        { type: 'kinematic' },
        { type: 'drag', drag: 2 },
        { type: 'fade', to: 0, tween: tweenPower2In },
      ],
    });

    // spawn emitter
    this.addChild(emitter.view);

    // aniamte
    await emitter.start();

    // despawn
    this.removeChild(emitter.view);
  }
}
